/* eslint-disable complexity */
import { Box, SxProps, Theme, Typography, SelectChangeEvent } from '@mui/material';
import React, { ComponentType, ForwardedRef, forwardRef, ReactElement, ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
	CoreSelect,
	SelectOption,
	CoreSelectProps,
	CoreSelectMenuItemProps,
	CoreSelectMenuItem,
} from '../core-select';
import {
	allValueStyles,
	coreConditionalSelectContainerStyles,
	emptyValueStyles,
	selectedCountLabelStyles,
	selectedCountNumberStyles,
	selectedCountWrapperStyles,
	selectedValueWithCountStyles,
	selectedValuesStyles,
} from './core-conditional-select.styles';
import { LogicalOperatorEnum, LogicalOperatorSelect, LogicalOperatorsList } from './logical-operator-select.component';
import { clsx } from '../../../utils';
import { IsIn, IsNotEmpty, IsString, IsUUID } from 'class-validator';
import { ArrowDownIcon } from '../../../icons';

export class ConditionalIdsDto {
	@IsIn(LogicalOperatorsList)
	public operator: LogicalOperatorEnum;

	@IsUUID('4', { each: true })
	public ids: string[];
}

export class CustomOperatorIdsDto<T = string> {
	@IsString()
	@IsNotEmpty()
	public operator: T;

	@IsUUID('4', { each: true })
	public ids: string[];
}


export interface OperatorProps {
	operator?: string;
	onChange?(operator: string): void;
}

export interface CoreConditionalSelectProps<
	Conditional extends boolean | undefined = undefined,
	UseCustomOperators extends boolean | undefined = undefined,
	CustomOperatorSelectProps extends OperatorProps = OperatorProps
> extends CoreSelectProps {
	allLabel?: string;
	allLabelMenuItem?: string;
	allLabelMenuItemSelected?: string;
	allLabelSx?: SxProps<Theme>;
	allLabelMenuItemSx?: SxProps<Theme>;
	conditional?: Conditional;
	useCustomOperators?: UseCustomOperators;
	CustomOperatorSelect?: ComponentType<CustomOperatorSelectProps>;
	emptyLabel?: string;
	value?: string[];
	options?: SelectOption[];
	operator?: Conditional extends false | undefined
		? undefined
		: UseCustomOperators extends false | undefined
		? LogicalOperatorEnum
		: CustomOperatorSelectProps['operator'];
	IconComponent?: ComponentType;
	MenuItemProps?: CoreSelectMenuItemProps;
	showCountOfSelected?: boolean;
	selectAllByDefault?: boolean;
	showOptions?: boolean;
	multiple?: boolean;
	renderCustomValue?(selectedOptions: SelectOption[], isAllSelected: boolean): ReactNode;
	onValueSelected?(
		values: Conditional extends false | undefined
			? string[]
			: UseCustomOperators extends false | undefined
			? ConditionalIdsDto
			: CustomOperatorIdsDto<CustomOperatorSelectProps['operator']>,
		isAutoSelect?: boolean
	): void;
	renderSelectedValues?(selectedOptions: SelectOption[]): React.ReactNode;
}

export const CoreConditionalSelect = forwardRef(<
	Conditional extends boolean | undefined = undefined,
	UseCustomOperators extends boolean | undefined = undefined,
	CustomOperatorSelectProps extends OperatorProps = OperatorProps
>(
	props: CoreConditionalSelectProps<Conditional, UseCustomOperators, CustomOperatorSelectProps>,
	ref: ForwardedRef<HTMLElement>,
) => {
	const {
		disabled,
		emptyLabel,
		allLabel,
		allLabelMenuItem,
		allLabelMenuItemSelected,
		allLabelSx,
		allLabelMenuItemSx,
		placeholder,
		options = [],
		conditional,
		operator,
		IconComponent = ArrowDownIcon,
		showCountOfSelected = false,
		MenuItemProps,
		selectAllByDefault = false,
		value = selectAllByDefault ? options.map((option) => option.value) : [],
		name,
		useCustomOperators: hasCustomOperators,
		CustomOperatorSelect,
		showOptions = true,
		multiple = true,
		MenuProps,
		onValueSelected,
		renderOption,
		renderCustomValue,
		renderSelectedValues,
		...selectProps
	} = props;

	const { isAllSelected, normalizedValues, selectedOptions } = useMemo(() => {
		const selectedOptions = options.filter(option => value.includes(option.value));

		return {
			isAllSelected: selectedOptions.length === options.length && selectedOptions.length !== 0,
			normalizedValues: selectedOptions.map(option => option.value),
			selectedOptions,
		};
	}, [options, value]);

	const handleChange = useCallback(
		(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
			const { value } = event.target;
			const isAllClicked = (child as ReactElement<{ value: string }>).props.value === 'all';

			let selectedValues: string[];

			if (isAllClicked) {
				selectedValues = isAllSelected ? [] : options.map((option) => option.value);
			} else {
				selectedValues = (typeof value === 'string' ? value.split(',') : value) as string[];
			}

			let result: string[] | ConditionalIdsDto | CustomOperatorIdsDto = selectedValues;

			if (conditional) {
				result = {
					operator: operator ?? LogicalOperatorEnum.AND,
					ids: selectedValues,
				};
			}

			onValueSelected?.(result as never);
		},
		[conditional, isAllSelected, onValueSelected, operator, options]
	);

	const handleOperatorSelect = useCallback(
		(operator: LogicalOperatorEnum | CustomOperatorSelectProps['operator']) => {
			onValueSelected?.({
				operator,
				ids: value,
			} as never);
		},
		[onValueSelected, value]
	);

	const renderSelectValue = useCallback(() => {
		if (renderCustomValue) {
			return renderCustomValue(selectedOptions, isAllSelected);
		}

		if (multiple && showCountOfSelected) {
			return (
				<Box sx={selectedValueWithCountStyles}>
					<Box className="placeholder" sx={selectedCountLabelStyles(disabled, selectedOptions.length)}>
						{emptyLabel || placeholder}
					</Box>
					<Box sx={selectedCountWrapperStyles(disabled, selectedOptions.length)}>
						<Typography sx={selectedCountNumberStyles(disabled, selectedOptions.length)}>
							{selectedOptions.length}
						</Typography>
					</Box>
				</Box>
			);
		}

		if (!selectedOptions.length) {
			return (
				<Typography className="placeholder" component="span" sx={emptyValueStyles(disabled)}>
					{emptyLabel || placeholder}
				</Typography>
			);
		}

		if (isAllSelected && allLabel && selectedOptions.length !== 1) {
			return (
				<Typography component="span" sx={clsx(allValueStyles(disabled), allLabelSx)}>
					{allLabel}
				</Typography>
			);
		}

		if (renderSelectedValues) {
			return renderSelectedValues(selectedOptions);
		}

		return (
			<Typography component="span" sx={selectedValuesStyles(disabled)}>
				{selectedOptions.map(option => option.label).join(', ')}
			</Typography>
		);
	}, [
		allLabel,
		allLabelSx,
		disabled,
		emptyLabel,
		isAllSelected,
		multiple,
		placeholder,
		renderCustomValue,
		renderSelectedValues,
		selectedOptions,
		showCountOfSelected,
	]);

	return (
		<CoreSelect
			{...selectProps}
			disabled={disabled}
			fitMenuWidth
			menuMinWidth={conditional ? 300 : undefined}
			IconComponent={IconComponent}
			value={normalizedValues}
			MenuItemProps={MenuItemProps}
			ref={ref}
			name={name}
			multiple={multiple}
			onChange={handleChange}
			renderValue={renderSelectValue}
			displayEmpty
			sx={clsx(
				{
					'& .MuiSelect-select': {
						display: 'inline',
					},
				},
				props.sx
			)}
			MenuProps={{
				...(MenuProps ?? {}),
				PaperProps: {
					...(MenuProps?.PaperProps ?? {}),
					sx: coreConditionalSelectContainerStyles(MenuProps?.PaperProps?.sx)
				}
			}}
			options={showOptions ? options : []}
			renderOption={option => (
				<CoreSelectMenuItem key={option.value} value={option.value} withCheckbox={multiple} noWrap {...MenuItemProps}>
					{renderOption?.(option) || option.label}
				</CoreSelectMenuItem>
			)}
		>
			{conditional && !hasCustomOperators && (
				<LogicalOperatorSelect operator={operator as LogicalOperatorEnum} onChange={handleOperatorSelect} />
			)}

			{conditional && hasCustomOperators && CustomOperatorSelect && (
				<CustomOperatorSelect
					{...({
						operator,
						onChange: handleOperatorSelect,
					} as CustomOperatorSelectProps)}
				/>
			)}

			{showOptions && multiple && options?.length > 1 && allLabel && (
				<CoreSelectMenuItem
					value="all"
					noWrap
					forceSelected={isAllSelected}
					withCheckbox={multiple}
					{...MenuItemProps}
					sx={clsx(MenuItemProps?.sx, allLabelMenuItemSx)}
				>
					{isAllSelected && allLabelMenuItemSelected ? allLabelMenuItemSelected : (allLabelMenuItem || allLabel)}
				</CoreSelectMenuItem>
			)}
		</CoreSelect>
	);
});

CoreConditionalSelect.displayName = 'CoreConditionalSelect';
